<!-- NoteCrudDropdown.vue -->
<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import EditNoteForm from "./EditNoteForm.vue";
import EditNoteObservationForm from "./EditNoteObservationForm.vue";
import MarkObservationAsRead from "./MarkObservationAsRead.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";
import NoteCardModal from "@/components/Note/NoteCardModal.vue";
// ── NOVO: guard de permissão por vínculo Membro-Titular ───────────────────────
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";

const isDropdownOpen = ref(false);
const dropdownRef = ref(null);

const showEditModal = ref(false);
const showObservationEditModal = ref(false);
const showReadConfirmationModal = ref(false);
const showPermissionViewModal = ref(false);
const showNoteCardModal = ref(false);

const selectedNoteId = ref(null);
const initialStep = ref(1);

const props = defineProps({
  noteId: Number,
  userId: Number,
  noteCreatedByUserId: Number,
  noteUpdatedByUserId: Number,
  textCreatedByUserId: Number,
  textUpdatedByUserId: Number,
  noteObservation: String,
  noteObservationType: String, // "Observation" | "Correct" | "Incorrect" | null
  noteIsObservationRead: Boolean,
  activityCreatedByUserId: Number,
  canEditNote: Boolean,
  isSysUser: { type: Boolean, default: false },
});

const emit = defineEmits(["request-delete"]);

// Combina grid do vínculo + opção "Somente conteúdo do titular": editar/excluir
// só valem para notas próprias em atividades criadas pelo titular.
const { canModifyNote } = useMemberRestrictions();

const memberCanEditNote = computed(() =>
  canModifyNote("edit", props.noteCreatedByUserId, props.activityCreatedByUserId)
);
const memberCanDeleteNote = computed(() =>
  canModifyNote(
    "delete",
    props.noteCreatedByUserId,
    props.activityCreatedByUserId
  )
);

function handleClickOutside(event) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isDropdownOpen.value = false;
  }
}

onMounted(() => document.addEventListener("click", handleClickOutside, true));
onBeforeUnmount(() =>
  document.removeEventListener("click", handleClickOutside, true)
);

const isNoteCreator = computed(
  () => props.userId === props.noteCreatedByUserId
);
const isTextCreator = computed(
  () => props.userId === props.textCreatedByUserId
);
const isActivityCreator = computed(
  () => props.userId === props.activityCreatedByUserId
);

// Duas camadas diferentes, de propósito:
//   • autoria/grupo decide se o item APARECE (nunca ofereceu "Editar" na nota de
//     outra pessoa, e isso não muda);
//   • o vínculo decide se ele fica ATIVO — bloqueado, o item continua na tela,
//     esmaecido e com o motivo no tooltip, em vez de sumir sem explicação.
const canShowEditNote = computed(
  () => isNoteCreator.value || props.isSysUser || !!props.canEditNote
);

const canShowDelete = computed(
  () => canShowEditNote.value || isActivityCreator.value || isTextCreator.value
);

// Motivo exibido no tooltip do item bloqueado pelo vínculo.
const blockedByMembershipHint =
  "Você só pode editar ou excluir suas próprias notas em atividades " +
  "preparadas pelo titular ou pelo círculo dele.";

// Há algo a ver na observação? Certo/Errado são sinalizações explícitas e valem
// mesmo sem descrição (mesma regra de UpdateObservationAsync no backend e do
// destaque colorido em NoteList). O texto continua valendo sozinho por causa de
// notas antigas, que podem ter descrição com observationType nulo.
const hasObservationSignal = computed(
  () =>
    (!!props.noteObservation && !!props.noteObservation.trim()) ||
    props.noteObservationType === "Correct" ||
    props.noteObservationType === "Incorrect"
);

const canShowViewObservation = computed(() => {
  return (
    hasObservationSignal.value &&
    (isNoteCreator.value || isTextCreator.value || isActivityCreator.value)
  );
});

const canShowEditObservation = computed(() => {
  return (
    (isTextCreator.value || isActivityCreator.value) && !isNoteCreator.value
  );
});

const canShowPermissionsView = computed(() => {
  return isNoteCreator.value || isTextCreator.value || isActivityCreator.value;
});

function closeDropdown() {
  isDropdownOpen.value = false;
}

function openViewObservationModal() {
  selectedNoteId.value = props.noteId;
  showReadConfirmationModal.value = true;
  closeDropdown();
}
function openEditModal() {
  selectedNoteId.value = props.noteId;
  initialStep.value = 1;
  showEditModal.value = true;
  closeDropdown();
}
function openObservationEditModal() {
  selectedNoteId.value = props.noteId;
  showObservationEditModal.value = true;
  closeDropdown();
}
function openPermissionViewModal() {
  selectedNoteId.value = props.noteId;
  showPermissionViewModal.value = true;
  closeDropdown();
}
function openNoteCardModal() {
  selectedNoteId.value = props.noteId;
  showNoteCardModal.value = true;
  closeDropdown();
}

function handleDeleteClick() {
  closeDropdown();
  emit("request-delete", props.noteId);
}
</script>

<template>
  <div ref="dropdownRef" class="action-cell-container">
    <div @click.stop="isDropdownOpen = !isDropdownOpen" class="cursor-pointer">
      <slot></slot>
    </div>

    <div v-if="isDropdownOpen" class="row-dropdown-menu row-dropdown-menu--below-end">
      <ul>
        <li @click.prevent.stop="openNoteCardModal">
          <i class="fas fa-eye"></i>
          <span>Ver nota</span>
        </li>

        <li
          v-if="canShowViewObservation"
          @click.prevent.stop="openViewObservationModal"
        >
          <i class="fas fa-eye"></i>
          <span>Ver Observação</span>
        </li>

        <li
          v-if="canShowPermissionsView"
          @click.prevent.stop="openPermissionViewModal"
        >
          <i class="fas fa-user-shield"></i>
          <span>Ver permissões</span>
        </li>

        <!-- ── NOVO: Editar condicionado ao guard de edit (via canShowEditNote) -->
        <li
          v-if="canShowEditNote"
          :class="{ 'item-blocked': !memberCanEditNote }"
          :title="memberCanEditNote ? null : blockedByMembershipHint"
          @click.prevent.stop="memberCanEditNote && openEditModal()"
        >
          <i class="fas fa-pencil-alt"></i>
          <span>Editar Nota</span>
        </li>

        <li
          v-if="canShowEditObservation"
          @click.prevent.stop="openObservationEditModal"
        >
          <i class="fas fa-comment-alt"></i>
          <span>{{
            hasObservationSignal ? "Editar Observação" : "Fazer Observação"
          }}</span>
        </li>

        <!-- ── NOVO: Apagar condicionado ao guard de delete (via canShowDelete) -->
        <li
          v-if="canShowDelete"
          class="danger-item"
          :class="{ 'item-blocked': !memberCanDeleteNote }"
          :title="memberCanDeleteNote ? null : blockedByMembershipHint"
          @click.prevent.stop="memberCanDeleteNote && handleDeleteClick()"
        >
          <i class="fas fa-trash-alt"></i>
          <span>Apagar</span>
        </li>
      </ul>
    </div>

    <Teleport to="body">
      <NoteCardModal
        v-if="showNoteCardModal"
        :show="showNoteCardModal"
        :noteId="selectedNoteId"
        :userId="props.userId"
        @close="
          () => {
            showNoteCardModal = false;
            selectedNoteId = null;
          }
        "
      />

      <!-- ── NOVO: EditNoteForm condicionado ao guard de edit ────────────────── -->
      <EditNoteForm
        v-if="showEditModal && memberCanEditNote"
        :show="showEditModal"
        :noteId="selectedNoteId"
        :initialStep="initialStep"
        :notifyViaEvent="true"
        @close="
          () => {
            showEditModal = false;
            selectedNoteId = null;
            initialStep = 1;
          }
        "
      />

      <EditNoteObservationForm
        v-if="showObservationEditModal"
        :showObservationForm="showObservationEditModal"
        :noteId="selectedNoteId"
        @close="
          () => {
            showObservationEditModal = false;
            selectedNoteId = null;
          }
        "
      />

      <MarkObservationAsRead
        v-if="showReadConfirmationModal"
        :showReadConfirmation="showReadConfirmationModal"
        :noteId="selectedNoteId"
        :isViewingOnly="props.noteIsObservationRead"
        @close="
          () => {
            showReadConfirmationModal = false;
            selectedNoteId = null;
          }
        "
      />

      <PermissionView
        v-if="showPermissionViewModal"
        :show="showPermissionViewModal"
        entityType="note"
        :noteId="selectedNoteId"
        title="Permissões da nota"
        @close="
          () => {
            showPermissionViewModal = false;
            selectedNoteId = null;
          }
        "
      />
    </Teleport>
  </div>
</template>

<style scoped>
.row-dropdown-menu {
  min-width: 150px;
}

/* Bloqueado pelo vínculo: continua listado, esmaecido e inerte. */
.row-dropdown-menu li.item-blocked,
.row-dropdown-menu li.item-blocked:hover,
.row-dropdown-menu li.danger-item.item-blocked:hover {
  opacity: 0.45;
  cursor: not-allowed;
  background-color: transparent;
}
</style>